import { Injectable, signal } from '@angular/core';

/**
 * Status of a tracked HTTP request.
 */
export type RequestStatus = 'pending' | 'success' | 'error';

/**
 * Request tracking entry for the debug-logger timeline view.
 *
 * Populated by {@link RequestTimelineService} (fed by `requestTimelineInterceptor`)
 * and rendered by `DebugLoggerComponent`'s "Request Timeline" panel.
 */
export interface RequestEntry {
  id: string;
  timestamp: Date;
  method: string;
  url: string;
  status: RequestStatus;
  statusCode?: number;
  duration?: number;
  error?: string;
}

/**
 * Signal-backed store of recent HTTP requests for the developer Request Timeline.
 *
 * This service is the single source of truth for request tracing. The
 * `requestTimelineInterceptor` calls {@link addRequest} when a request leaves
 * and {@link completeRequest} when the response (or error) arrives; the
 * `DebugLoggerComponent` reads the {@link requests} signal to render the
 * timeline. Keeping the store here (rather than on the component) means the
 * panel is fed by real production traffic instead of being dead UI.
 *
 * The buffer is bounded to {@link MAX_REQUESTS} most-recent entries to avoid
 * unbounded memory growth on long-lived sessions.
 */
@Injectable({
  providedIn: 'root'
})
export class RequestTimelineService {
  /** Maximum number of request entries to retain (newest first). */
  private readonly MAX_REQUESTS = 100;

  /** Monotonic counter used to mint unique request IDs. */
  private requestCounter = 0;

  /** Internal writable store of tracked requests (newest first). */
  private readonly _requests = signal<RequestEntry[]>([]);

  /** Read-only view of tracked requests for consumers. */
  readonly requests = this._requests.asReadonly();

  /**
   * Record the start of an HTTP request and return its tracking ID.
   *
   * @param method HTTP verb (e.g. `GET`, `POST`).
   * @param url Request URL.
   * @returns Unique ID to pass to {@link completeRequest}.
   */
  addRequest(method: string, url: string): string {
    const id = `req-${++this.requestCounter}`;
    const entry: RequestEntry = {
      id,
      timestamp: new Date(),
      method,
      url,
      status: 'pending'
    };

    this._requests.update(reqs => {
      const next = [entry, ...reqs];
      return next.length > this.MAX_REQUESTS ? next.slice(0, this.MAX_REQUESTS) : next;
    });

    return id;
  }

  /**
   * Mark a previously-added request as completed (success or error).
   *
   * @param id ID returned by {@link addRequest}.
   * @param statusCode HTTP status code of the response.
   * @param duration Round-trip duration in milliseconds.
   * @param error Optional error message; presence flips the entry to `error`.
   */
  completeRequest(id: string, statusCode: number, duration: number, error?: string): void {
    this._requests.update(reqs =>
      reqs.map(req =>
        req.id === id
          ? {
              ...req,
              status: error ? ('error' as const) : ('success' as const),
              statusCode,
              duration,
              error
            }
          : req
      )
    );
  }

  /** Remove all tracked requests. */
  clear(): void {
    this._requests.set([]);
  }
}
